import React, { useState, useEffect } from "react";

import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { BlogArticleTile } from "@project-stories/blog/BlogArticleTile/BlogArticleTile";
import { Button } from "@project-stories/elements/Button/Button";
import { Headings } from "@arctheme-components/elements/Headings/Headings";

const GridLayout = ({ initialArticles, settings }) => {
  const initialPerPage = settings?.globals?.articles_per_page || 6;
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(initialPerPage);
  const [articles, setArticles] = useState(initialArticles || []);
  const [currentArticles, setCurrentArticles] = useState(
    initialArticles?.slice(0, articlesPerPage) || [],
  );

  const { section, blocks, globals } = settings;
  const contentBlock = blocks?.find((block) => block.type === "content_block");

  const showPagination = !!settings?.globals?.articles_per_page;

  useEffect(() => {
    const handleArticlesComplete = (event) => {
      setCurrentPage(1);
      setArticles(event.detail.articles);
    };

    const handleNextPage = (event) => {
      if (event.detail.type === "pagination") {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("blog-articles-complete", handleArticlesComplete);
    window.addEventListener("nextpage", handleNextPage);

    return () => {
      window.removeEventListener(
        "blog-articles-complete",
        handleArticlesComplete,
      );
      window.removeEventListener("nextpage", handleNextPage);
    };
  }, []);

  useEffect(() => {
    // If no pagination, show all articles
    if (!showPagination) {
      setCurrentArticles(articles);
      return;
    }
    
    const indexOfLastArticle = currentPage * articlesPerPage;
    const newArticles = articles.slice(0, indexOfLastArticle);
    setCurrentArticles(newArticles);
  }, [currentPage, articles, articlesPerPage, showPagination]);


  const contentBlockRender = () => (
    <div className="flex flex-col space-y-[16px]">
      <Paragraph
        text={settings.section.title}
        type={isMobile ? 'h5' : settings?.section?.heading_type}
      />
      <Paragraph
        text={settings?.section?.subtext}
        type={settings?.section?.subtext_type}
      />
    </div>
  );

  const isReverse = isMobile
    ? section.layout_type_mobile === 'grid_reverse'
    : section.layout_type === 'grid_reverse';

  return (
    <div className={`w-full color-${section.color_schema}`}>
      <div className={"flex justify-center"}>
        <div className={"flex-item flex"}>
          <div className={"section-toprow__details flex"}>
            {contentBlockRender()}
          </div>
        </div>
        <div className={"flex-item flex"}>
          {contentBlock?.settings.cta_text && (
            <Button
              link={contentBlock?.settings.cta_url}
              label={contentBlock?.settings.cta_text}
              style={contentBlock?.settings.cta_style}
            />
          )}
        </div>
      </div>

      <div
        className="blog-articles flex flex-col pt-6"
        x-data="pagination"
        x-init={`init(${articles.length}, ${articlesPerPage}, true, 'articles')`}
      >
        {currentArticles.length > 0 && (
          <>
            <div className={`article-itemsgap-[8px] lg:gap-[20px]`}>


              <div className=" grid grid-cols-1 lg:grid-cols-3 gap-x-[30px] gap-y-8 lg:gap-y-[60px]">
                {currentArticles.map((article, index) => {

                  return (
                    <div
                      className="blog-articles__article article"
                      key={index}
                    >
                      <BlogArticleTile
                        article={article}
                        show_excerpt={true}
                        settings={settings}
                        showContent={true}
                        showCategory={true}
                        show_author={section.show_author}
                        show_read_time={section.show_read_time}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            {showPagination && (
              <div
                x-ref="paginationContainer"
                className="flex justify-center items-center space-x-4 pt-[64px] lg:pt-[87px]"
              ></div>
            )}
          </>
        )}
        {currentArticles.length <= 0 && (
          <div className={"blog-articles-empty"}>
            <Paragraph
              text={"There are no articles found with that filter."}
              type={"b2"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GridLayout;
